import type { MaybeRef } from "vue"
import type { ServerResponseV1 } from "@/http"
import type { ISaveWishlistArgs, IWishlistResponse, IWishlistSaveErrorResponse } from "@/types/wishlist.interface"
import { useMutation, useQueryClient } from "@tanstack/vue-query"
import { toValue } from "vue"
import { v1 } from "@/axios"
import { ValidationException } from "@/http"
import { Wishlist } from "@/models/wishlist"
import { GET_WISHLIST_QUERY_KEY } from "./getWishlist"

function getWishlistSaveErrorResponse(error: unknown): IWishlistSaveErrorResponse | null {
  if (error instanceof ValidationException || (error && typeof error === "object" && "response" in error)) {
    const data = (error as { response?: { data?: unknown } }).response?.data

    if (data && typeof data === "object" && ("errors" in data || "message" in data))
      return data as IWishlistSaveErrorResponse
  }

  return null
}

export function getInvalidWishlistLinkUrls(error: unknown): string[] | null {
  const data = getWishlistSaveErrorResponse(error)

  if (!data)
    return null

  return (data.errors ?? []).flatMap((item) => {
    const url = item.source?.parameter?.trim()

    return url ? [url] : []
  })
}

export async function saveWishlist(args: ISaveWishlistArgs, eventVaultId: number) {
  const response = await v1.post<ServerResponseV1<IWishlistResponse>>(`/events/${eventVaultId}/wishlist`, args)

  return Wishlist.fromResponse(response.data.data)
}

export const SAVE_WISHLIST_MUTATION_KEY = "saveWishlist"

export function useSaveWishlist(eventVaultId?: MaybeRef<number | undefined>) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [SAVE_WISHLIST_MUTATION_KEY, eventVaultId],
    mutationFn: (args: ISaveWishlistArgs) => saveWishlist(args, toValue(eventVaultId)!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [GET_WISHLIST_QUERY_KEY, eventVaultId] })
    },
  })
}
